import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  FollowMemberFollowedEnvelope,
  FollowMemberUnfollowedEnvelope,
  ListCurrentMemberFollowersOptions,
  ListCurrentMemberFollowersResult,
  ListCurrentMemberFollowingOptions,
  ListCurrentMemberFollowingResult,
  ListMemberFollowersOptions,
  ListMemberFollowersResult,
  ListMemberFollowingOptions,
  ListMemberFollowingResult,
  QueryMemberConnectionsResponse,
  QueryMyMemberConnectionsResponse,
  followMember as universalFollowMember,
  listCurrentMemberFollowers as universalListCurrentMemberFollowers,
  listCurrentMemberFollowing as universalListCurrentMemberFollowing,
  listMemberFollowers as universalListMemberFollowers,
  listMemberFollowing as universalListMemberFollowing,
  queryCurrentMemberConnections as universalQueryCurrentMemberConnections,
  queryMemberConnections as universalQueryMemberConnections,
  unfollowMember as universalUnfollowMember,
} from './members-v3-follow-member-followers.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function followMember(httpClient: HttpClient): FollowMemberSignature {
  return (memberId: string) =>
    universalFollowMember(
      memberId,
      // @ts-ignore
      { httpClient }
    );
}

interface FollowMemberSignature {
  /**
   * Sets the current member to follow another, specified member.
   * @param - ID of the member to follow.
   */
  (memberId: string): Promise<void>;
}

export function unfollowMember(
  httpClient: HttpClient
): UnfollowMemberSignature {
  return (memberId: string) =>
    universalUnfollowMember(
      memberId,
      // @ts-ignore
      { httpClient }
    );
}

interface UnfollowMemberSignature {
  /**
   * Sets the current member to unfollow another, specified member.
   * @param - ID of the member to unfollow.
   */
  (memberId: string): Promise<void>;
}

export function listCurrentMemberFollowing(
  httpClient: HttpClient
): ListCurrentMemberFollowingSignature {
  return (options?: ListCurrentMemberFollowingOptions) =>
    universalListCurrentMemberFollowing(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCurrentMemberFollowingSignature {
  /**
   * Retrieves a list of members who are followed by the current member.
   */
  (
    options?: ListCurrentMemberFollowingOptions
  ): Promise<ListCurrentMemberFollowingResult>;
}

export function listMemberFollowing(
  httpClient: HttpClient
): ListMemberFollowingSignature {
  return (memberId: string, options?: ListMemberFollowingOptions) =>
    universalListMemberFollowing(
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMemberFollowingSignature {
  /**
   * Retrieves a list of members who are followed by the given member.
   * @param - ID of the member whose followers to list.
   */
  (
    memberId: string,
    options?: ListMemberFollowingOptions
  ): Promise<ListMemberFollowingResult>;
}

export function listCurrentMemberFollowers(
  httpClient: HttpClient
): ListCurrentMemberFollowersSignature {
  return (options?: ListCurrentMemberFollowersOptions) =>
    universalListCurrentMemberFollowers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCurrentMemberFollowersSignature {
  /**
   * Retrieves a list of members who are following the current member.
   */
  (
    options?: ListCurrentMemberFollowersOptions
  ): Promise<ListCurrentMemberFollowersResult>;
}

export function listMemberFollowers(
  httpClient: HttpClient
): ListMemberFollowersSignature {
  return (memberId: string, options?: ListMemberFollowersOptions) =>
    universalListMemberFollowers(
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMemberFollowersSignature {
  /**
   * Retrieves a list of members who are following the given member.
   * @param - ID of the member whose followed members to list.
   */
  (
    memberId: string,
    options?: ListMemberFollowersOptions
  ): Promise<ListMemberFollowersResult>;
}

export function queryCurrentMemberConnections(
  httpClient: HttpClient
): QueryCurrentMemberConnectionsSignature {
  return (connectedMemberIds: string[]) =>
    universalQueryCurrentMemberConnections(
      connectedMemberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCurrentMemberConnectionsSignature {
  /**
   * Retrieves a list of members and their connections to the current member.
   * @param - List of member IDs whose connections to the current member will be retrieved.
   */
  (connectedMemberIds: string[]): Promise<
    NonNullablePaths<
      QueryMyMemberConnectionsResponse,
      | `connectedMembers`
      | `connectedMembers.${number}.connectedMemberId`
      | `connectedMembers.${number}.followedByMember`
      | `connectedMembers.${number}.followingMember`
    >
  >;
}

export function queryMemberConnections(
  httpClient: HttpClient
): QueryMemberConnectionsSignature {
  return (memberId: string, connectedMemberIds: string[]) =>
    universalQueryMemberConnections(
      memberId,
      connectedMemberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMemberConnectionsSignature {
  /**
   * Retrieves a list of members and their connections to the given member.
   * > **Note:** If an empty array is passed as `connectedMemberIds`, the call will succeed, but it will not return any data.
   * @param - Member ID.
   * @param - List of member IDs whose connections to the given member will be retrieved.
   */
  (memberId: string, connectedMemberIds: string[]): Promise<
    NonNullablePaths<
      QueryMemberConnectionsResponse,
      | `connectedMembers`
      | `connectedMembers.${number}.connectedMemberId`
      | `connectedMembers.${number}.followedByMember`
      | `connectedMembers.${number}.followingMember`
    >
  >;
}

export const onFollowMemberFollowed = EventDefinition(
  'wix.members.v3.follow_member_followed',
  true,
  (event: FollowMemberFollowedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<FollowMemberFollowedEnvelope>();
export const onFollowMemberUnfollowed = EventDefinition(
  'wix.members.v3.follow_member_unfollowed',
  true,
  (event: FollowMemberUnfollowedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<FollowMemberUnfollowedEnvelope>();

export {
  ActionEvent,
  App,
  BaseEventMetadata,
  ConnectedMembers,
  CursorPaging,
  Cursors,
  CustomTag,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  FollowMemberFollowedEnvelope,
  FollowMemberRequest,
  FollowMemberResponse,
  FollowMemberUnfollowedEnvelope,
  Follower,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ListCurrentMemberFollowersOptions,
  ListCurrentMemberFollowersResult,
  ListCurrentMemberFollowingOptions,
  ListCurrentMemberFollowingResult,
  ListMemberFollowersOptions,
  ListMemberFollowersRequest,
  ListMemberFollowersResponse,
  ListMemberFollowersResult,
  ListMemberFollowingOptions,
  ListMemberFollowingRequest,
  ListMemberFollowingResponse,
  ListMemberFollowingResult,
  ListMyMemberFollowersRequest,
  ListMyMemberFollowersResponse,
  ListMyMemberFollowingRequest,
  ListMyMemberFollowingResponse,
  MemberFollowed,
  MemberUnfollowed,
  MessageEnvelope,
  Page,
  PagingMetadataV2,
  QueryMemberConnectionsRequest,
  QueryMemberConnectionsResponse,
  QueryMyMemberConnectionsRequest,
  QueryMyMemberConnectionsResponse,
  RestoreInfo,
  URI,
  UnfollowMemberRequest,
  UnfollowMemberResponse,
  WebhookIdentityType,
} from './members-v3-follow-member-followers.universal.js';
